import React from 'react';
import PropTypes from 'prop-types';
import { Card as MuiCard } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  operationalComponentSurfaces,
  operationalElevation,
  operationalRadii
} from '../../../design-system/tokens';

/**
 * Card - Componente atômico base para exibição de conteúdo em cards
 *
 * Este componente segue o princípio do Atomic Design como um "átomo".
 * Fornece uma base consistente para cards no sistema, com suporte a estado de seleção.
 *
 * Variantes disponíveis:
 * - detailed: Card com visual detalhado (padrão)
 * - list: Card em formato de lista
 */
const Card = ({
  children,
  selected = false,
  onClick,
  variant = 'detailed',
  className = '',
  favorite = false,
  read = false,
  sentiment = 'neutral',
  glassmorphism = false,
  sx: externalSx,  // Extrair sx explicitamente para não ser sobrescrito
  ...props  // Agora props NÃO inclui sx
}) => {
  const theme = useTheme();

  return (
    <MuiCard
      elevation={0}
      className={`base-card ${variant} ${selected ? 'selected' : ''} ${favorite ? 'favorite' : ''} ${read ? 'read' : ''} ${sentiment} ${glassmorphism ? 'glassmorphism' : ''} ${className}`}
      onClick={onClick}
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: operationalComponentSurfaces.stage,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${operationalRadii.surface}px`,
        boxShadow: operationalElevation.surface,
        zIndex: 10,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: operationalElevation.surface,
        },
        ...(selected && {
          borderColor: theme.palette.primary.main,
          zIndex: 20,
        }),
        ...externalSx  // Aplicar sx externo por último
      }}
      {...props}  // Agora não inclui sx, então não sobrescreve
    >
      {children}
    </MuiCard>
  );
};

Card.propTypes = {
  /** Conteúdo do card */
  children: PropTypes.node,
  /** Se o card está selecionado */
  selected: PropTypes.bool,
  /** Função chamada quando o card é clicado */
  onClick: PropTypes.func,
  /** Variante do card */
  variant: PropTypes.oneOf(['detailed', 'list']),
  /** Classes CSS adicionais */
  className: PropTypes.string,
  /** Estilos adicionais */
  sx: PropTypes.object,
  /** Se o card está desabilitado */
  disabled: PropTypes.bool,
  /** Elevação do card */
  elevation: PropTypes.number,
  /** Se o card é favorito */
  favorite: PropTypes.bool,
  /** Se o card foi lido */
  read: PropTypes.bool,
  /** Sentimento associado ao card */
  sentiment: PropTypes.oneOf(['positive', 'neutral', 'negative']),
  /** Se o card deve ter efeito glassmorphism */
  glassmorphism: PropTypes.bool
};

export default Card;
